'use client';

// Révélation au SCROLL (IntersectionObserver) — complément de `.rmbr-reveal`, qui ne joue lui
// qu'au chargement. Ici l'animation se déclenche quand l'élément entre dans le viewport, ce qui
// donne son rythme à une page longue : chaque section se compose à l'arrivée du regard.
//
// Usage : `const ref = useReveal<HTMLDivElement>();` puis `<div ref={ref} className="rmbr-rise">`.
// La classe `.rmbr-rise` part masquée ; l'observer pose `data-revealed="true"` qui joue l'entrée.
// Un seul déclenchement (on n'annule jamais) : re-scroller vers le haut ne fait pas clignoter.
//
// Respect de `prefers-reduced-motion` : la règle CSS neutralise la transition (contenu visible
// immédiatement), donc rien à gérer ici — l'attribut est posé de la même façon.

import { useEffect, useRef } from 'react';

interface RevealOptions {
  // Fraction de l'élément visible avant déclenchement (0–1). Bas pour les grands blocs.
  threshold?: number;
  // Marge du viewport : négatif en bas = déclenche un peu APRÈS l'entrée (évite le tir trop tôt).
  rootMargin?: string;
  // Délai avant révélation (ms) — stagger d'un groupe sans dupliquer d'observers.
  delay?: number;
}

export function useReveal<T extends HTMLElement>({
  threshold = 0.12,
  rootMargin = '0px 0px -8% 0px',
  delay = 0,
}: RevealOptions = {}) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Filet de sécurité : sans IntersectionObserver (très vieux navigateur, tests), on révèle
    // tout de suite — le contenu ne doit JAMAIS rester invisible à cause d'une animation.
    if (typeof IntersectionObserver === 'undefined') {
      node.dataset.revealed = 'true';
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        observer.disconnect(); // one-shot
        if (delay > 0) {
          timer = setTimeout(() => {
            node.dataset.revealed = 'true';
          }, delay);
        } else {
          node.dataset.revealed = 'true';
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [threshold, rootMargin, delay]);

  return ref;
}
